import {
  Accordion,
  Alert,
  Badge,
  Card,
  Group,
  SimpleGrid,
  Stack,
  Table,
  Tabs,
  Text,
} from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import { AdminActionButton } from "./AdminActionButton";
import { AlertMarkers, ForwardKeyBadge, ForwardOutcomeBadge } from "./AlertMarkers";
import { DataTable } from "./DataTable";
import { FieldRow } from "./DetailGrid";
import { AdminProjectionErrorState, GenerationBadge } from "./GenerationBadge";
import { Money } from "./Money";
import { ExternalMono, Mono } from "./Mono";
import { RelatedLink } from "./RelatedLink";
import { RelatedList } from "./RelatedList";
import { StatusBadge } from "./StatusBadge";
import { Timestamp } from "./Timestamp";
import { EmptyState, ErrorState } from "./states";
import { forwardResidualNow, notApplicable, resendResidualMovePrompt } from "@/api/actions";
import { col } from "@/entities/columns";
import { COLLECTIONS } from "@/firestore/collections";
import {
  DOCUMENT_ID,
  useFirestoreDoc,
  useFirestoreInQuery,
  useFirestoreQuery,
} from "@/firestore/hooks";
import type { LiveQueryState } from "@/firestore/hooks";
import type {
  ResidualForwardDoc,
  SessionKeyDoc,
  SmartAccountOperationDoc,
  WalletAvailabilityDoc,
  WalletDoc,
  WalletKeyInventoryDoc,
  WalletMigrationDoc,
  WithDocId,
} from "@/firestore/types";
import { txUrl } from "@/lib/explorer";
import { useNow } from "@/lib/useNow";
import {
  ACCOUNT_ROLES,
  RESIDUAL_FLOOR_MICRO_USDC,
  TRIGGER_LABELS,
  forwardKeyStates,
  forwardKeysOnOperableWallet,
  forwardNowAvailability,
  isPreviousWallet,
  migrationOfSource,
  movePromptAvailability,
  residualAlerts,
  residualForwardOperationId,
} from "@/wallet/residuals";

type Forward = WithDocId<ResidualForwardDoc>;

function forwardColumns(operations: ReadonlyMap<string, WithDocId<SmartAccountOperationDoc>>) {
  const opOf = (f: Forward) => {
    const id = residualForwardOperationId(f);
    return id ? { id, sao: operations.get(id) ?? null } : null;
  };
  return [
    col<Forward>({
      id: "createdAt",
      header: "Created",
      value: (r) => r.createdAt,
      cell: (r) => <Timestamp value={r.createdAt} />,
    }),
    col<Forward>({
      id: "role",
      header: "Account",
      value: (r) => r.role,
      cell: (r) => (
        <Text size="sm" tt="capitalize">
          {r.role}
        </Text>
      ),
    }),
    col<Forward>({
      id: "trigger",
      header: "Trigger",
      value: (r) => TRIGGER_LABELS[r.trigger] ?? r.trigger,
      cell: (r) => <Text size="sm">{TRIGGER_LABELS[r.trigger] ?? r.trigger}</Text>,
    }),
    col<Forward>({
      id: "outcome",
      header: "Outcome",
      value: (r) => `${r.state} ${r.skipReason ?? ""}`,
      cell: (r) => (
        <Group gap={4} wrap="nowrap">
          <StatusBadge state={r.state} />
          <ForwardOutcomeBadge forward={r} />
        </Group>
      ),
    }),
    col<Forward>({
      id: "amount",
      header: "Amount",
      value: (r) => r.amountMicroUsdc,
      cell: (r) => <Money micro={r.amountMicroUsdc} size="sm" />,
    }),
    col<Forward>({
      id: "destination",
      header: "To wallet",
      value: (r) => r.destinationWallet ?? "",
      cell: (r) => <RelatedLink entity="wallet" id={r.destinationWallet} />,
    }),
    col<Forward>({
      id: "tx",
      header: "Transaction",
      value: (r) => opOf(r)?.sao?.transactionHash ?? "",
      cell: (r) => {
        const op = opOf(r);
        if (!op) return <Text c="dimmed">—</Text>;
        return (
          <Stack gap={0}>
            <ExternalMono value={op.sao?.transactionHash} href={txUrl(op.sao?.transactionHash)} />
            <Group gap={4} wrap="nowrap">
              <RelatedLink entity="smartAccountOperation" id={op.id} />
              {op.sao ? <StatusBadge state={op.sao.state} /> : null}
            </Group>
          </Stack>
        );
      },
    }),
    col<Forward>({
      id: "failure",
      header: "Failure",
      value: (r) => r.failureReason ?? "",
      cell: (r) => (
        <Text size="sm" c={r.failureReason ? "red" : "dimmed"} maw={220}>
          {r.failureReason ?? "—"}
        </Text>
      ),
    }),
    col<Forward>({
      id: "coveredThrough",
      header: "Covers credits up to",
      value: (r) => r.coveredThrough,
      cell: (r) => <Timestamp value={r.coveredThrough} />,
    }),
  ];
}

/**
 * A previous wallet's `residualForwards` (`wallet == id order by createdAt desc`, index
 * `residualForwards__wallet_createdAtDesc`), each joined to its smart-account operation (id
 * derived from the forward's, `documentId() in` batches) for its state and transaction.
 */
function ForwardsTable({ forwards }: { forwards: LiveQueryState<Forward[]> }) {
  const operations = useFirestoreInQuery<SmartAccountOperationDoc>(
    COLLECTIONS.smartAccountOperations,
    DOCUMENT_ID,
    forwards.data.map((f) => residualForwardOperationId(f)),
  );
  const byId = new Map(operations.data.map((o) => [o._docId, o]));
  if (forwards.error)
    return (
      <AdminProjectionErrorState
        error={forwards.error}
        title="Residual forwards not available in this environment yet"
        collections="residualForwards is a collection"
        release="added by wallet-service 0.78.0 (with its residualForwards__wallet_createdAtDesc index)"
      />
    );
  return (
    <DataTable
      data={forwards.data}
      columns={forwardColumns(byId)}
      loading={forwards.loading}
      searchable={false}
      emptyState={
        <EmptyState
          title="No forwards"
          description="Nothing was forwarded from this wallet (forwarding needs wallet-service 0.78.0; a skipped row is written only for an inflow, the arming, the cleanup, a Move or Forward now)."
        />
      }
    />
  );
}

function PreviousWalletItem({
  wallet,
  viewedWalletId,
  migrations,
  currentWalletId,
  now,
}: {
  wallet: WithDocId<WalletDoc>;
  viewedWalletId: string;
  migrations: readonly WithDocId<WalletMigrationDoc>[];
  currentWalletId: string | null;
  now: number;
}) {
  const availability = useFirestoreDoc<WalletAvailabilityDoc>(
    COLLECTIONS.walletAvailability,
    wallet._docId,
  );
  // Admin-only (0.74.0): only its `walletDeletedAt` is read here. An undeployed collection is tolerated.
  const inventory = useFirestoreDoc<WalletKeyInventoryDoc>(
    COLLECTIONS.walletKeyInventories,
    wallet._docId,
  );
  const keys = useFirestoreQuery<SessionKeyDoc>(COLLECTIONS.sessionKeys, {
    filters: [["account", "==", wallet._docId]],
  });
  const forwards = useFirestoreQuery<ResidualForwardDoc>(COLLECTIONS.residualForwards, {
    filters: [["wallet", "==", wallet._docId]],
    orderBy: [["createdAt", "desc"]],
    limit: 50,
  });
  const migration = migrationOfSource(migrations, wallet._docId);
  const states = forwardKeyStates({ wallet, migration, keys: keys.data, now });
  const deleted = !!inventory.data?.walletDeletedAt;
  const balances = {
    main: availability.data?.mainBalanceMicroUsdc ?? null,
    reserve: availability.data?.reserveBalanceMicroUsdc ?? null,
  };
  const total = availability.data ? (balances.main ?? 0) + (balances.reserve ?? 0) : null;
  const alerts = residualAlerts({
    wallet,
    deleted,
    balances,
    forwards: forwards.data,
    keys: keys.data,
    migrations,
    now,
  });
  const forwardNow = forwardNowAvailability({ wallet, deleted, states });
  const move = movePromptAvailability({ wallet, deleted, currentWalletId, balances, states });
  const successor = wallet.migratedToWallet ?? migration?.newWallet ?? null;

  return (
    <Accordion.Item value={wallet._docId} data-testid={`previous-wallet-${wallet._docId}`}>
      <Accordion.Control>
        <Group gap="sm" wrap="nowrap">
          {/* The control is a button: no copy button (a nested button) inside it. */}
          <Mono value={wallet._docId} short copy={false} />
          {wallet._docId === viewedWalletId ? (
            <Badge variant="outline" color="gray" radius="sm" tt="none">
              this wallet
            </Badge>
          ) : null}
          <StatusBadge state={deleted ? "closed" : wallet.state} />
          <GenerationBadge generation={wallet.contractGeneration} />
          <Text size="sm">Residual</Text>
          <Money
            micro={total}
            size="sm"
            fw={700}
            c={total !== null && total >= RESIDUAL_FLOOR_MICRO_USDC ? "orange" : undefined}
          />
          <AlertMarkers markers={alerts} />
        </Group>
      </Accordion.Control>
      <Accordion.Panel>
        <Stack gap="md">
          <Group justify="space-between" align="flex-start">
            <SimpleGrid cols={{ base: 2, sm: 3, lg: 6 }} spacing="sm" style={{ flex: 1 }}>
              <FieldRow label="Wallet" value={<RelatedLink entity="wallet" id={wallet._docId} />} />
              <FieldRow
                label="Migrated to"
                value={<RelatedLink entity="wallet" id={successor} />}
              />
              <FieldRow
                label="Migration"
                value={
                  <Group gap={4} wrap="nowrap">
                    <RelatedLink entity="walletMigration" id={migration?._docId} />
                    {migration ? <StatusBadge state={migration.state} /> : null}
                  </Group>
                }
              />
              <FieldRow
                label="Completed"
                value={<Timestamp value={migration?.completedAt} absolute />}
              />
              <FieldRow label="Main" value={<Money micro={balances.main} />} />
              <FieldRow label="Reserve" value={<Money micro={balances.reserve} />} />
            </SimpleGrid>
            <Group gap="xs">
              <AdminActionButton
                action={
                  forwardNow.offered
                    ? forwardResidualNow
                    : notApplicable(forwardResidualNow, forwardNow.note)
                }
                params={{ walletId: wallet._docId, role: forwardNow.role }}
              />
              <AdminActionButton
                action={
                  move.offered
                    ? resendResidualMovePrompt
                    : notApplicable(resendResidualMovePrompt, move.note)
                }
                params={{ walletId: wallet._docId }}
              />
            </Group>
          </Group>
          {availability.data ? (
            <Text size="xs" c="dimmed">
              Balances as of <Timestamp value={availability.data.computedAt} /> (walletAvailability;
              the commands read the chain).
            </Text>
          ) : (
            <Text size="xs" c="dimmed">
              No walletAvailability snapshot for this wallet.
            </Text>
          )}

          <Table withTableBorder verticalSpacing="xs" data-testid="forward-keys">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Forward key</Table.Th>
                <Table.Th>State</Table.Th>
                <Table.Th>Expires</Table.Th>
                <Table.Th>Key</Table.Th>
                <Table.Th>Recipient (successor account)</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {ACCOUNT_ROLES.map((role) => {
                const state = states[role];
                return (
                  <Table.Tr key={role}>
                    <Table.Td>
                      <Text size="sm" tt="capitalize">
                        {role}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <ForwardKeyBadge state={state} />
                    </Table.Td>
                    <Table.Td>
                      <Timestamp value={state.expiresAt} absolute />
                    </Table.Td>
                    <Table.Td>
                      <RelatedLink entity="sessionKey" id={state.sessionKeyId} />
                    </Table.Td>
                    <Table.Td>
                      <Mono value={state.key?.recipientAddress} short copy />
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
          {keys.error ? <ErrorState error={keys.error} /> : null}

          <Tabs defaultValue="forwards" keepMounted={false}>
            <Tabs.List>
              <Tabs.Tab value="forwards">Forwards ({forwards.data.length})</Tabs.Tab>
              <Tabs.Tab value="receive">Receive addresses</Tabs.Tab>
              <Tabs.Tab value="bank">Bank details</Tabs.Tab>
            </Tabs.List>
            <Tabs.Panel value="forwards" pt="sm">
              <ForwardsTable forwards={forwards} />
            </Tabs.Panel>
            <Tabs.Panel value="receive" pt="sm">
              <Stack gap="sm">
                <Text size="sm" c="dimmed">
                  At completion each active relay receive address is superseded by the
                  successor&apos;s for the same route. A superseded address still settles into the
                  old Main (relay cannot revoke it), and the credit is forwarded.
                </Text>
                <RelatedList
                  spec={{
                    title: "Deposit addresses",
                    entity: "depositAddress",
                    collection: COLLECTIONS.depositAddresses,
                    fkField: "walletId",
                    value: wallet._docId,
                    orderBy: [["createdAt", "desc"]],
                  }}
                />
                <RelatedList
                  spec={{
                    title: "USDT receive addresses",
                    entity: "stablecoinReceiveAddress",
                    collection: COLLECTIONS.stablecoinReceiveAddresses,
                    fkField: "walletId",
                    value: wallet._docId,
                    orderBy: [["createdAt", "desc"]],
                  }}
                />
              </Stack>
            </Tabs.Panel>
            <Tabs.Panel value="bank" pt="sm">
              <Stack gap="sm">
                <Text size="sm" c="dimmed">
                  The old virtual account stays on this wallet until its migration&apos;s completion
                  + 90 days (expiresAt), keeps paying the old forwarder (swept into the old Main,
                  then forwarded), and its route is deleted by the daily expiry. A VA the owner
                  creates on the current wallet stamps this one supersededBy.
                </Text>
                <RelatedList
                  spec={{
                    title: "Virtual accounts (old bank details)",
                    entity: "virtualAccount",
                    collection: COLLECTIONS.virtualAccounts,
                    fkField: "account",
                    value: wallet._docId,
                    orderBy: [["createdAt", "desc"]],
                  }}
                />
              </Stack>
            </Tabs.Panel>
          </Tabs>
        </Stack>
      </Accordion.Panel>
    </Accordion.Item>
  );
}

function createdKey(w: WalletDoc): number {
  const parsed = Date.parse(w.originalCreatedAt ?? w.createdAt);
  return Number.isNaN(parsed) ? 0 : parsed;
}

/**
 * Wallet 360's Previous wallets panel (AD-1c): the owner's `migrated` and `retired` wallets, each
 * with its balances, forward keys (signed / pending / expired / missing, with the expiry), the
 * `residualForwards` history, superseded receive addresses and old bank details, plus [Forward now]
 * and [Re-send Move prompt]. The current wallet's own bank details close the panel. Rendered only
 * when the owner has a previous wallet, or when this wallet carries a forward key it should not.
 */
export function PreviousWalletsCard({ wallet }: { wallet: WithDocId<WalletDoc> }) {
  const now = useNow();
  const ownerWallets = useFirestoreQuery<WalletDoc>(COLLECTIONS.wallets, {
    filters: [["owner", "==", wallet.owner]],
    limit: 50,
  });
  const migrations = useFirestoreQuery<WalletMigrationDoc>(COLLECTIONS.walletMigrations, {
    filters: [["owner", "==", wallet.owner]],
    orderBy: [["createdAt", "desc"]],
    limit: 50,
  });
  const viewedKeys = useFirestoreQuery<SessionKeyDoc>(COLLECTIONS.sessionKeys, {
    filters: [["account", "==", wallet._docId]],
  });

  const previous = ownerWallets.data
    .filter((w) => isPreviousWallet(w))
    .sort((a, b) => createdKey(b) - createdKey(a));
  const currentWalletId =
    wallet.currentWallet ??
    ownerWallets.data.find((w) => w.currentWallet)?.currentWallet ??
    ownerWallets.data.find((w) => w.state === "provisioned")?._docId ??
    null;
  const operable = isPreviousWallet(wallet)
    ? []
    : forwardKeysOnOperableWallet({ wallet, keys: viewedKeys.data, migrations: migrations.data });

  if (ownerWallets.error) {
    return (
      <Card withBorder radius="md" padding="lg">
        <Text fw={600} mb="xs">
          Previous wallets
        </Text>
        <ErrorState error={ownerWallets.error} />
      </Card>
    );
  }
  if (previous.length === 0 && operable.length === 0) return null;

  return (
    <Card withBorder radius="md" padding="lg" data-testid="previous-wallets">
      <Group justify="space-between" mb="xs">
        <Text fw={600}>Previous wallets</Text>
        <Text size="sm" c="dimmed">
          {previous.length}
        </Text>
      </Group>
      <Text size="sm" c="dimmed" mb="md" maw={860}>
        Wallets this owner migrated away from (or retired). Money that reaches one is forwarded to
        the successor with the migration&apos;s forward keys for 90 days after completion, then only
        by the owner&apos;s one-tap Move. Forward keys are never re-prompted: once one lapsed,
        re-send the one-shot Move prompt. The security alerts on forwarding (armMismatch) are in the
        logs only (residual_forward_security).
      </Text>
      {operable.length > 0 ? (
        <Alert
          color="red"
          variant="light"
          icon={<IconAlertTriangle size={16} />}
          title="Forward key on an operable wallet"
          mb="md"
        >
          This wallet is {wallet.state} but carries {operable.length} pending or enabled forward
          key(s) ({operable.map((k) => k.type).join(", ")}): the abandon should have revoked them.
          The daily expiry revokes them and pages forwardKeyOnOperableWallet (CRITICAL).
        </Alert>
      ) : null}
      {migrations.error ? <ErrorState error={migrations.error} /> : null}
      {previous.length > 0 ? (
        <Accordion
          multiple
          defaultValue={[previous[0]._docId]}
          variant="separated"
          chevronPosition="left"
        >
          {previous.map((w) => (
            <PreviousWalletItem
              key={w._docId}
              wallet={w}
              viewedWalletId={wallet._docId}
              migrations={migrations.data}
              currentWalletId={currentWalletId}
              now={now}
            />
          ))}
        </Accordion>
      ) : null}
      {currentWalletId && !previous.some((w) => w._docId === currentWalletId) ? (
        <Stack gap="xs" mt="md">
          <Group gap="xs">
            <Text size="sm" fw={600}>
              Current wallet
            </Text>
            <RelatedLink entity="wallet" id={currentWalletId} />
          </Group>
          <RelatedList
            spec={{
              title: "Virtual accounts on the current wallet (new bank details)",
              entity: "virtualAccount",
              collection: COLLECTIONS.virtualAccounts,
              fkField: "account",
              value: currentWalletId,
              orderBy: [["createdAt", "desc"]],
            }}
          />
        </Stack>
      ) : null}
    </Card>
  );
}
